import { partials } from "@shopify/partial-rendering";
import { transition } from "@theme/view-transition";

// <s-product-recommendations> lazily fetches related/complementary products and swaps them
// into its {% partial %} region using the base theme's partial-rendering stack — the same
// @shopify/partial-rendering + view-transition that <s-live-form> (filters) is built on.
// One component per file, framework-free.
//
// When the block was already rendered in the recommendations context (data-performed="true"),
// the server markup is kept and no fetch happens. Otherwise, once the block nears the
// viewport, it fetches data-url and applies the named partial, replacing the skeletons.
class ProductRecommendations extends HTMLElement {
  #observer = null;
  #controller = null;

  connectedCallback() {
    if (this.dataset.performed === "true") {
      this.#fill();
      this.#syncVisibility();
      return;
    }
    // Show the manual picks in the pending rail right away, before Shopify's recommendations arrive.
    this.#fill();
    if (!this.dataset.url || !this.dataset.productId || !this.dataset.partial) return;

    this.#observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0]?.isIntersecting) return;
        observer.disconnect();
        this.#load();
      },
      { rootMargin: "0px 0px 400px 0px" },
    );
    this.#observer.observe(this);
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#controller?.abort();
  }

  async #load() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    const url = `${this.dataset.url}&product_id=${encodeURIComponent(this.dataset.productId)}`;
    const name = this.dataset.partial;

    this.setAttribute("aria-busy", "true");
    try {
      // Fetch the named {% partial %} region from the recommendations endpoint and apply it.
      const html = await partials.fetch(name, { url, signal });
      await transition(() => partials.apply(html));
      this.dataset.performed = "true";
      this.#fill();
      this.#syncVisibility();
    } catch (error) {
      if (error.name === "AbortError") return;
      // The HTML partial route can be rejected (e.g. 422). Fall back to the JSON recommendations
      // endpoint and build cards from the <template> markup the product template provides.
      try {
        await this.#loadJson(url, signal);
      } catch (jsonError) {
        if (jsonError.name === "AbortError") return;
        // Recommendations unavailable: show the backfill cards alone, or hide the block if there are none.
        this.#fill();
        this.hidden = !this.querySelector("[data-scroller-track] [data-product-id]");
      }
    } finally {
      this.removeAttribute("aria-busy");
      this.#controller = null;
    }
  }

  async #loadJson(url, signal) {
    const railTemplate = this.querySelector("template[data-rec-fallback]");
    const cardTemplate = this.querySelector("template[data-rec-fallback-card]");
    if (!railTemplate || !cardTemplate) throw new Error("No fallback templates");

    const jsonUrl = url.replace(/\/recommendations\/products(?=\?|$)/, "/recommendations/products.json");
    const response = await fetch(jsonUrl, { signal, headers: { Accept: "application/json" } });
    if (!response.ok) throw new Error(`Recommendations JSON failed: ${response.status}`);
    const data = await response.json();
    const products = (data.products || []).filter((product) => product && product.url);
    if (products.length === 0) {
      this.#fill();
      this.dataset.performed = "true";
      this.hidden = !this.querySelector("[data-scroller-track] [data-product-id]");
      return;
    }

    const rail = railTemplate.content.cloneNode(true);
    const track = rail.querySelector("[data-scroller-track]");
    for (const product of products) {
      track.append(this.#buildCard(cardTemplate, product));
    }

    this.#insertRail(rail);
    this.#fill();
    this.dataset.performed = "true";
    this.hidden = false;
  }

  // Swaps the partial region (skeletons or an empty response) for a built rail.
  #insertRail(rail) {
    const region = partials.getAll(this).find(({ name }) => name === this.dataset.partial);
    if (region) {
      region.range.deleteContents();
      region.range.insertNode(rail);
    } else {
      this.append(rail);
    }
  }

  // Tops the rail up to data-limit with the product template's backfill cards (same collection first),
  // skipping any product Shopify already recommended. Builds the rail when Shopify returned nothing.
  #fill() {
    const filler = this.querySelector("template[data-rec-filler]");
    const limit = Number(this.dataset.limit) || 0;
    if (!filler || !limit) return;

    let track = this.querySelector("[data-scroller-track]");
    let pendingRail = null;
    if (!track) {
      const railTemplate = this.querySelector("template[data-rec-fallback]");
      if (!railTemplate) return;
      pendingRail = railTemplate.content.cloneNode(true);
      track = pendingRail.querySelector("[data-scroller-track]");
      if (!track) return;
    }

    // Manual related products (Search & Discovery) go first, in the merchant's order; automatic picks follow.
    let added = this.#prioritize(track, limit);
    const shown = new Set(Array.from(track.querySelectorAll("[data-product-id]"), (card) => card.dataset.productId));
    const candidates = Array.from(filler.content.children).filter((card) => card.dataset.productId);
    for (const card of candidates) {
      if (shown.size >= limit) break;
      if (shown.has(card.dataset.productId)) continue;
      track.append(document.importNode(card, true));
      shown.add(card.dataset.productId);
      added += 1;
    }
    if (added === 0) return;

    if (pendingRail) this.#insertRail(pendingRail);
    // Let <s-scroller> recompute its overflow state for the new items.
    track.dispatchEvent(new Event("scroll"));
  }

  // Moves the manual cards to the front (replacing any duplicate automatic card) and trims the rail to the limit.
  // Returns how many cards it placed so #fill knows to refresh the scroller.
  #prioritize(track, limit) {
    const manual = this.querySelector("template[data-rec-manual]");
    if (!manual) return 0;
    const cards = Array.from(manual.content.children).filter((card) => card.dataset.productId);
    if (cards.length === 0) return 0;
    const ids = new Set(cards.map((card) => card.dataset.productId));
    track.querySelectorAll("[data-product-id]").forEach((card) => {
      if (ids.has(card.dataset.productId)) card.remove();
    });
    track.prepend(...cards.map((card) => document.importNode(card, true)));
    Array.from(track.children)
      .filter((card) => card.dataset.productId)
      .slice(limit)
      .forEach((card) => card.remove());
    return cards.length;
  }

  #buildCard(cardTemplate, product) {
    const card = cardTemplate.content.cloneNode(true);
    if (card.firstElementChild && product.id) card.firstElementChild.dataset.productId = String(product.id);
    const href = product.url;
    card.querySelectorAll("[data-rec-link]").forEach((link) => {
      link.href = href;
      if (link.hasAttribute("aria-label")) link.setAttribute("aria-label", product.title);
    });
    card.querySelector("article")?.setAttribute("aria-label", product.title);

    const { main, hover } = this.#cardImages(product, cardTemplate.dataset.whitebgMode);
    const setImage = (el, src, alt) => {
      if (!el) return;
      if (!src) {
        el.remove();
        return;
      }
      const sized = (width) => {
        const imageUrl = new URL(src, window.location.href);
        imageUrl.searchParams.set("width", String(width));
        return imageUrl.toString();
      };
      el.src = sized(600);
      el.srcset = [360, 600, 900].map((width) => `${sized(width)} ${width}w`).join(", ");
      el.alt = alt;
    };
    setImage(card.querySelector("[data-rec-img]"), main, product.title);
    setImage(card.querySelector("[data-rec-hover]"), hover && hover !== main ? hover : "", "");

    const title = card.querySelector("[data-rec-title]");
    if (title) title.textContent = product.title;

    // Matches the Liquid cards: label products that belong to the Selected collection.
    const selectedIds = (cardTemplate.dataset.selectedIds || "").split(",").filter(Boolean);
    const selectedLabel = card.querySelector("[data-rec-selected]");
    if (selectedLabel) {
      if (selectedIds.includes(String(product.id)) && selectedLabel.textContent.trim()) {
        selectedLabel.hidden = false;
      } else {
        selectedLabel.remove();
      }
    }

    const priceEl = card.querySelector("[data-rec-price]");
    const compareEl = card.querySelector("[data-rec-compare]");
    if (priceEl) {
      const varies = product.price_varies && product.price_min !== product.price_max;
      priceEl.textContent = varies
        ? `${this.#money(product.price_min)} – ${this.#money(product.price_max)}`
        : this.#money(product.price);
    }
    if (compareEl && product.compare_at_price && product.compare_at_price > product.price && !product.price_varies) {
      compareEl.textContent = this.#money(product.compare_at_price);
      compareEl.hidden = false;
    }
    return card;
  }

  // Mirrors snippets/card-image-pick.liquid. Returns { main, hover } image URLs.
  // Unless the white-background mode is "off": main = the white-background image (file name contains "WhiteBG") tagged
  // with one of the first available variant's option values, else the first white-background image; hover = the first
  // other image with the same alt tag as main, else the first image that is not a white-background image.
  // Otherwise (or with no white-background image): main = the featured image, hover = the second image.
  #cardImages(product, mode) {
    const featured = typeof product.featured_image === "string" ? product.featured_image : product.featured_image?.src;
    const media = Array.isArray(product.media) && product.media.length
      ? product.media
          .filter((item) => !item.media_type || item.media_type === "image")
          .map((item) => ({ src: item.src || item.preview_image?.src, alt: item.alt || "" }))
      : (product.images || []).map((image) => (typeof image === "string" ? { src: image, alt: "" } : { src: image?.src, alt: image?.alt || "" }));
    const images = media.filter((item) => item.src);
    const plain = { main: featured || images[0]?.src, hover: images[1]?.src };
    if (!mode || mode === "off") return plain;
    const fileName = (url) => String(url || "").split("?")[0].split("/").pop().toLowerCase();
    const isWhite = (item) => fileName(item.src).includes("whitebg");
    const white = images.filter(isWhite);
    if (white.length === 0) return plain;
    const tagOf = (item) => item.alt.split("|")[0].trim().toLowerCase();
    const variants = product.variants || [];
    const variant = variants.find((item) => item.available) || variants[0];
    const values = (variant?.options || []).map((value) => String(value).trim().toLowerCase());
    const main = white.find((item) => tagOf(item) && values.includes(tagOf(item))) || white[0];
    const others = images.filter((item) => !isWhite(item));
    const mainTag = tagOf(main);
    const hover = (mainTag && others.find((item) => tagOf(item) === mainTag)) || others[0];
    return { main: main.src, hover: hover?.src };
  }

  #money(cents) {
    const currency = window.Shopify?.currency?.active || this.dataset.currency || "USD";
    const locale = document.documentElement.lang || undefined;
    return new Intl.NumberFormat(locale, { style: "currency", currency }).format((Number(cents) || 0) / 100);
  }

  #syncVisibility() {
    const region = partials.getAll(this).find(({ name }) => name === this.dataset.partial);
    const content = region?.range.cloneContents();
    this.hidden = !content || (!content.firstElementChild && !content.textContent.trim());
  }
}

if (!customElements.get("s-product-recommendations")) {
  customElements.define("s-product-recommendations", ProductRecommendations);
}
